import { useState, useMemo } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import styled from 'styled-components';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { GenericErrorState } from '../GenericErrorState';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { buildInvoiceDetailsUrl } from '../../utils/invoiceUrls';
import { useInvoicesData } from '../../hooks/useInvoicesData';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { trackInteraction } from '../../utils/usageTracker';
import { applyResponsiveColumnWidths } from '../../utils/responsiveTableColumns';
import { useWindowSize } from '../../hooks/useWindowSize';
import { PaginatedDataTable, DEFAULT_PAGE_SIZE } from '../PaginatedDataTable';
import { StatusLabel } from '../StatusLabel';
import { jsx as _jsx } from "react/jsx-runtime";
const ClickableRow = styled.tr.withConfig({
  displayName: "InvoicesTable__ClickableRow"
})({
  cursor: 'pointer'
});
function InvoiceRowComponent({
  row,
  trRef,
  requiredRowProps,
  children
}) {
  const routerHistory = useHistory();
  return /*#__PURE__*/_jsx(ClickableRow, Object.assign({}, requiredRowProps, {
    ref: trRef,
    "data-test-id": "invoice-row",
    onClick: row.invoiceLink ? () => {
      trackInteraction('invoice-row-clicked');
      routerHistory.push(buildInvoiceDetailsUrl(row));
    } : undefined,
    children: children
  }));
}
const SORTABLE_PROPERTIES = ['invoiceNumber', 'dueDate', 'status', 'amountBilled', 'amountDue'];
const COLUMNS = [{
  columnName: 'invoiceNumber',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.number"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    children: row.invoiceNumber
  })
}, {
  columnName: 'dueDate',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.dueDate"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    children: typeof row.dueDate === 'number' && /*#__PURE__*/_jsx(FormattedDateTime, {
      value: row.dueDate,
      format: "ll"
    })
  })
}, {
  columnName: 'status',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.status"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(StatusLabel, {
      status: row.status,
      domain: "invoice"
    })
  })
}, {
  columnName: 'amountBilled',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.amountBilled"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    "data-test-id": "invoice-amount-billed",
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amountBilled),
      currencyCode: row.currencyCode
    })
  })
}, {
  columnName: 'amountDue',
  sortable: true,
  HeaderContentComponent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.columns.balanceDue"
  }),
  CellComponent: ({
    row
  }) => /*#__PURE__*/_jsx("td", {
    "data-test-id": "invoice-amount-due",
    children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: Number(row.amountDue),
      currencyCode: row.currencyCode
    })
  })
}];
const COLUMN_WIDTH_WEIGHTS = [5, 3, 3, 3, 3];
export function InvoicesTable() {
  var _invoicesData$totalRe;
  const [sort, setSort] = useState(undefined);
  const {
    width: windowWidth
  } = useWindowSize();
  const columns = useMemo(() => applyResponsiveColumnWidths(COLUMNS, windowWidth, COLUMN_WIDTH_WEIGHTS), [windowWidth]);
  const {
    currentOffset,
    currentPage,
    hasPrev,
    handleNextPage,
    handlePrevPage,
    resetPagination
  } = useCursorPagination();
  const sortProperty = SORTABLE_PROPERTIES.find(property => property === (sort === null || sort === void 0 ? void 0 : sort.columnName));
  const sortDirection = sort ? sort.direction === 1 ? 'ASC' : 'DESC' : undefined;
  const {
    loading: invoicesLoading,
    error: invoicesError,
    data: invoicesData
  } = useInvoicesData(Object.assign({
    pageLength: DEFAULT_PAGE_SIZE
  }, currentOffset !== undefined && {
    offset: currentOffset
  }, sortProperty !== undefined && {
    sortProperty
  }, sortDirection !== undefined && {
    sortDirection
  }));
  const handleSortsChange = nextSorts => {
    setSort(nextSorts[0]);
    resetPagination();
  };
  const sorts = useMemo(() => sort ? [sort] : [], [sort]);
  const hasMore = Boolean(invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.hasMore);
  const nextOffset = invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.offset;
  if (invoicesError) {
    return /*#__PURE__*/_jsx(GenericErrorState, {
      inline: true
    });
  }
  if ((invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.results.length) === 0) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.empty"
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "invoices-table",
    children: /*#__PURE__*/_jsx(PaginatedDataTable, {
      columns: columns,
      rows: (invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.results) || [],
      getRowId: row => {
        var _row$objectId;
        return String((_row$objectId = row.objectId) !== null && _row$objectId !== void 0 ? _row$objectId : row.invoiceNumber);
      },
      RowComponent: InvoiceRowComponent,
      loading: invoicesLoading,
      currentPage: currentPage,
      hasMore: hasMore,
      hasPrev: hasPrev,
      onNextPage: () => nextOffset && handleNextPage(nextOffset),
      onPrevPage: handlePrevPage,
      sorts: sorts,
      onSortsChange: handleSortsChange,
      totalResultsCount: (_invoicesData$totalRe = invoicesData === null || invoicesData === void 0 ? void 0 : invoicesData.totalResults) !== null && _invoicesData$totalRe !== void 0 ? _invoicesData$totalRe : 0
    })
  });
}